import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Lock, User } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error("Please enter both username and password");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem("admin_token", response.data.access_token);
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 opacity-0 animate-fade-up">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
            <span 
              className="font-bold text-2xl tracking-wide text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              AUREALIAN
            </span>
          </div>
          <p className="text-[#A1A1AA] text-sm uppercase tracking-widest">
            Admin Portal
          </p>
        </div>

        {/* Login Form */}
        <form 
          onSubmit={handleSubmit}
          className="bg-[#121212] border border-[#27272A]/40 rounded-sm p-8 space-y-6 opacity-0 animate-fade-up animation-delay-100"
          data-testid="admin-login-form"
        >
          <div className="text-center mb-2">
            <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <h1 
              className="text-xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Sign In
            </h1>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
              Username
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
              <Input
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                placeholder="Enter username"
                required
                data-testid="input-username"
                className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-12 text-white placeholder:text-[#52525B] pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
              <Input
                name="password"
                type="password"
                value={credentials.password}
                onChange={handleInputChange}
                placeholder="Enter password"
                required
                data-testid="input-password"
                className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-12 text-white placeholder:text-[#52525B] pl-10"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            data-testid="login-btn"
            className="w-full bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 rounded-none uppercase tracking-widest text-sm font-bold py-6 gold-glow disabled:opacity-50"
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </Button>

          <p className="text-center text-xs text-[#52525B]">
            Protected area. Authorized access only.
          </p>
        </form>

        {/* Back to site */}
        <div className="text-center mt-6 opacity-0 animate-fade-up animation-delay-200">
          <Button
            variant="link"
            onClick={() => navigate("/")}
            className="text-[#A1A1AA] hover:text-[#D4AF37] text-sm"
            data-testid="back-to-site-btn"
          >
            &larr; Back to site
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
